/* ────────────────────────────────────────────────────────────
   Types
──────────────────────────────────────────────────────────── */
export interface ArticleProps {
  id: string;
  num: string | number;
  title: React.ReactNode;
  children: React.ReactNode;
  headerBlue?: boolean;
}

export interface TableRow {
  [key: string]: React.ReactNode;
}

export interface PageShellProps {
  title: string;
  announcedAt: string;
  effectiveAt: string;
  intro?: React.ReactNode;
  tocItems: { href: string; label: string }[];
  children: React.ReactNode;
}

/* ────────────────────────────────────────────────────────────
   Shared UI Components
──────────────────────────────────────────────────────────── */
export function Article({
  id,
  num,
  title,
  children,
  headerBlue = false
}: ArticleProps) {
  return (
    <div
      id={id}
      className="bg-white border border-slate-300 rounded-xl mb-4 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <div
        className={`flex items-center gap-3 px-7 py-4 border-b ${headerBlue ? 'bg-blue-50' : ''}`}
      >
        <span className="shrink-0 text-white text-xs font-bold px-2.5 py-0.5 rounded bg-blue-700">
          {num}
        </span>
        <span className="text-[15px] font-bold text-slate-800 flex-1">
          {title}
        </span>
      </div>
      <div className="px-7 py-5">{children}</div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="bg-blue-100 text-blue-900 font-bold text-[12px] px-3 py-2.5 border border-blue-200 text-left whitespace-nowrap">
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td className="px-3 py-2.5 border border-slate-300 text-[12px] text-slate-700 align-top leading-relaxed">
      {children}
    </td>
  );
}

export function DataTable({
  headers,
  rows
}: {
  headers: string[];
  rows: TableRow[];
}) {
  return (
    <div className="overflow-x-auto mt-3.5 rounded-lg border border-slate-300">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {headers.map((h) => (
              <Th key={h}>{h}</Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 1 ? 'bg-slate-50/70' : 'bg-white'}>
              {headers.map((h) => (
                <Td key={h}>{row[h]}</Td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Clause({
  num,
  children
}: {
  num: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2.5 mb-3.5 last:mb-0">
      <span className="font-semibold text-blue-600 min-w-[22px] shrink-0 text-sm pt-0.5">
        {num}
      </span>
      <span className="text-slate-700 text-sm leading-relaxed">{children}</span>
    </div>
  );
}

export function SubItem({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2 mb-1.5 text-sm text-slate-500 leading-snug">
      <span className="text-blue-400 min-w-[16px]">•</span>
      <span>{children}</span>
    </div>
  );
}

export function SubOrderItem({
  num,
  children
}: {
  num: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2 mb-1.5 text-sm text-slate-500 leading-snug">
      <span className="text-blue-400 min-w-[16px]">{num}</span>
      <span>{children}</span>
    </div>
  );
}

type CalloutVariant = 'blue' | 'green' | 'yellow' | 'red';

const calloutStyles: Record<CalloutVariant, string> = {
  blue: 'bg-blue-50 border-blue-500 text-blue-900',
  green: 'bg-emerald-50 border-emerald-500 text-emerald-900',
  yellow: 'bg-amber-50 border-amber-400 text-amber-900',
  red: 'bg-red-50 border-red-500 text-red-900'
};

export function Callout({
  children,
  variant = 'blue'
}: {
  children: React.ReactNode;
  variant?: CalloutVariant;
}) {
  return (
    <div
      className={`mt-3.5 border-l-4 rounded-r-lg px-4 py-3 text-sm leading-relaxed ${calloutStyles[variant]}`}
    >
      {children}
    </div>
  );
}

export function EmailLink({ email }: { email: string }) {
  return (
    <a
      href={`mailto:${email}`}
      className="text-blue-600 hover:underline font-medium"
    >
      {email}
    </a>
  );
}

export function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[14px] font-bold text-blue-900 mt-5 mb-2.5 pb-1.5 border-b border-dashed border-slate-300 first:mt-0">
      {children}
    </p>
  );
}

export function PageShell({
  title,
  announcedAt,
  effectiveAt,
  intro,
  tocItems,
  children
}: PageShellProps) {
  return (
    <div>
      <div className="max-w-4xl mx-auto px-2 sm:px-5 py-12 pb-20">
        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight mb-4">
            {title}
          </h1>
          <div className="inline-flex border border-slate-300 rounded-lg overflow-hidden text-sm divide-x divide-slate-300">
            <div className="flex items-center gap-2 px-2 sm:px-5  py-2.5">
              <span className="text-slate-400 font-medium text-xs">공고일</span>
              <span className="text-blue-600 font-semibold">{announcedAt}</span>
            </div>
            <div className="flex items-center gap-2 px-2 sm:px-5 py-2.5">
              <span className="text-slate-400 font-medium text-xs">시행일</span>
              <span className="text-blue-600 font-semibold">{effectiveAt}</span>
            </div>
          </div>
        </div>

        {/* Intro */}
        {intro && (
          <div className="bg-white border border-slate-300 border-l-4 border-l-blue-600 rounded-r-xl px-6 py-5 mb-8 text-sm leading-loose text-slate-700">
            {intro}
          </div>
        )}

        {/* TOC */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-7 py-6 mb-10">
          <p className="text-xs font-bold text-blue-800 uppercase tracking-widest mb-4">
            목차
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {tocItems.map((item) => (
              <li key={item.href}>
                <a
                  href={item.href}
                  className="text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline transition-colors"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {children}
      </div>
    </div>
  );
}
